import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import "@/pages/ProjectDetail.css";

// Reusable editorial project page. The `project` prop comes from
// `data/projectDetails.js`. The component walks each section in
// order and renders the matching layout — kept in one file so the
// rhythm between sections stays cohesive and easy to refine.

const useScrollReveal = (rootRef, reducedMotion) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll("[data-pd-reveal]");
    if (reducedMotion || !("IntersectionObserver" in window)) {
      targets.forEach((n) => n.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -8% 0px" }
    );
    targets.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [rootRef, reducedMotion]);
};

const Img = ({ base, src, alt, className, sizes, eager = false }) => (
  <img
    src={base + src}
    alt={alt}
    className={className}
    loading={eager ? "eager" : "lazy"}
    decoding="async"
    sizes={sizes}
  />
);

// ---------- Section renderers --------------------------------------

const ImmersiveSequence = ({ data, base }) => (
  <section
    className="pd__sequence"
    aria-label="Immersive living sequence"
    data-testid="pd-immersive"
  >
    <figure
      className="pd__seq-large"
      data-pd-reveal
    >
      <Img base={base} src={data.images[0]} alt="Lit corner of the living room" className="pd__img" />
    </figure>
    <div className="pd__seq-row">
      <figure className="pd__seq-mid" data-pd-reveal>
        <Img base={base} src={data.images[1]} alt="Soft seating" className="pd__img" />
      </figure>
      <figure
        className="pd__seq-mid pd__seq-mid--offset"
        data-pd-reveal
        style={{ transitionDelay: "180ms" }}
      >
        <Img base={base} src={data.images[2]} alt="Threshold view into the home" className="pd__img" />
      </figure>
    </div>
    <p className="pd__interlude" data-pd-reveal>
      {data.caption}
    </p>
  </section>
);

const PairedDetails = ({ data, base }) => (
  <section className="pd__details" aria-label={data.title} data-testid="pd-details">
    <header className="pd__section-head">
      <h2 className="pd__section-title" data-pd-reveal>
        {data.title}
      </h2>
      <p className="pd__section-line" data-pd-reveal style={{ transitionDelay: "140ms" }}>
        {data.line}
      </p>
    </header>
    <div className="pd__details-pair">
      <figure className="pd__detail-cell" data-pd-reveal>
        <Img base={base} src={data.pair[0]} alt="Material and pattern detail" className="pd__img" />
      </figure>
      <figure
        className="pd__detail-cell"
        data-pd-reveal
        style={{ transitionDelay: "180ms" }}
      >
        <Img base={base} src={data.pair[1]} alt="Light and surface detail" className="pd__img" />
      </figure>
    </div>
    <figure className="pd__detail-support" data-pd-reveal>
      <Img base={base} src={data.support} alt="Quiet supporting detail" className="pd__img" />
    </figure>
  </section>
);

const PrivateRooms = ({ data, base }) => (
  <section className="pd__private" aria-label="Private rooms" data-testid="pd-private">
    <p className="pd__section-line pd__section-line--alone" data-pd-reveal>
      {data.line}
    </p>
    <figure className="pd__private-opening" data-pd-reveal>
      <Img base={base} src={data.opening} alt="Private room, opening" className="pd__img" />
    </figure>
    <figure
      className="pd__private-lamplit"
      data-pd-reveal
      style={{ transitionDelay: "120ms" }}
    >
      <Img base={base} src={data.lamplit} alt="Lamplit private room" className="pd__img" />
    </figure>
    <div className="pd__private-pair">
      <figure className="pd__private-cell" data-pd-reveal>
        <Img base={base} src={data.pair[0]} alt="Patterned headboard detail" className="pd__img" />
      </figure>
      <figure
        className="pd__private-cell"
        data-pd-reveal
        style={{ transitionDelay: "180ms" }}
      >
        <Img base={base} src={data.pair[1]} alt="Door and colour detail" className="pd__img" />
      </figure>
    </div>
    {data.closing && (
      <figure className="pd__private-closing" data-pd-reveal>
        <Img base={base} src={data.closing} alt="Quiet detail in a private room" className="pd__img" />
      </figure>
    )}
  </section>
);

const Gather = ({ data, base }) => (
  <section className="pd__gather" aria-label={data.title} data-testid="pd-gather">
    <header className="pd__section-head">
      <h2 className="pd__section-title" data-pd-reveal>
        {data.title}
      </h2>
      <p className="pd__section-line" data-pd-reveal style={{ transitionDelay: "140ms" }}>
        {data.line}
      </p>
    </header>
    <figure className="pd__gather-hero" data-pd-reveal>
      <Img base={base} src={data.hero} alt="Dining table and gathering room" className="pd__img" />
    </figure>
    <div className="pd__gather-pair">
      <figure className="pd__gather-cell" data-pd-reveal>
        <Img base={base} src={data.pair[0]} alt="Dining detail" className="pd__img" />
      </figure>
      <figure
        className="pd__gather-cell"
        data-pd-reveal
        style={{ transitionDelay: "180ms" }}
      >
        <Img base={base} src={data.pair[1]} alt="Dining alternate view" className="pd__img" />
      </figure>
    </div>
  </section>
);

const FunctionSection = ({ data, base }) => (
  <section className="pd__function" aria-label="Function" data-testid="pd-function">
    <p className="pd__section-line pd__section-line--alone" data-pd-reveal>
      {data.line}
    </p>
    <figure className="pd__function-main" data-pd-reveal>
      <Img base={base} src={data.main} alt="Kitchen, the design language continued" className="pd__img" />
    </figure>
    <div className="pd__function-row">
      <figure className="pd__function-vertical" data-pd-reveal>
        <Img base={base} src={data.vertical} alt="Passage / threshold" className="pd__img" />
      </figure>
      <figure
        className="pd__function-detail"
        data-pd-reveal
        style={{ transitionDelay: "180ms" }}
      >
        <Img base={base} src={data.detail} alt="Quiet functional detail" className="pd__img" />
      </figure>
    </div>
  </section>
);

const Process = ({ data, base }) => (
  <section className="pd__process" aria-label={data.title} data-testid="pd-process">
    <div className="pd__process-top">
      <header className="pd__process-head">
        <h2 className="pd__section-title" data-pd-reveal>
          {data.title}
        </h2>
        <p
          className="pd__section-line"
          data-pd-reveal
          style={{ transitionDelay: "140ms" }}
        >
          {data.line}
        </p>
      </header>
      <figure className="pd__process-main" data-pd-reveal>
        <Img base={base} src={data.main.src} alt="Concept sketch sheet" className="pd__img pd__img--paper" />
        {data.main.label && <figcaption className="pd__label">{data.main.label}</figcaption>}
      </figure>
    </div>

    <div className="pd__process-pair">
      {data.pair.map((p, i) => (
        <figure
          key={p.src}
          className="pd__process-cell"
          data-pd-reveal
          style={{ transitionDelay: i === 1 ? "180ms" : "0ms" }}
        >
          <Img
            base={base}
            src={p.src}
            alt={p.label || "Process image"}
            className={"pd__img" + (p.src.startsWith("sketch/") ? " pd__img--paper" : "")}
          />
          {p.label && <figcaption className="pd__label">{p.label}</figcaption>}
        </figure>
      ))}
    </div>

    <div className="pd__process-strip" data-pd-reveal style={{ "--strip-cols": data.strip.length }}>
      {data.strip.map((p) => (
        <figure key={p.src} className="pd__process-tile">
          <Img
            base={base}
            src={p.src}
            alt="Archival process image"
            className={"pd__img" + (p.src.startsWith("sketch/") ? " pd__img--paper" : "")}
          />
        </figure>
      ))}
    </div>
  </section>
);

const DesignNotes = ({ data }) => (
  <section className="pd__notes" aria-label="Design notes" data-testid="pd-notes">
    <h2 className="pd__section-title pd__section-title--center" data-pd-reveal>
      {data.title}
    </h2>
    <div className="pd__notes-grid">
      {data.notes.map((n, i) => (
        <div
          key={n.label}
          className="pd__note"
          data-pd-reveal
          style={{ transitionDelay: `${i * 140}ms` }}
        >
          <span className="pd__note-label">{n.label}</span>
          <p className="pd__note-text">{n.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Closing = ({ data, base }) => (
  <section
    className={"pd__closing" + (data.image ? "" : " pd__closing--text-only")}
    aria-label="Closing"
    data-testid="pd-closing"
  >
    <p className="pd__closing-line" data-pd-reveal>
      {data.line}
    </p>
    {data.image && (
      <figure className="pd__closing-figure" data-pd-reveal style={{ transitionDelay: "200ms" }}>
        <Img base={base} src={data.image} alt="A quiet closing memory of the home" className="pd__img" />
      </figure>
    )}
  </section>
);

const NextNav = ({ next }) => (
  <nav className="pd__next" aria-label="Project navigation" data-testid="pd-next">
    <Link to="/work" className="pd__back" data-testid="pd-back">
      <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
        <path
          d="M21 5H1M5 1L1 5l4 4"
          stroke="currentColor"
          strokeWidth="1"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Back to Selected Work
    </Link>
    {next && (
      <Link to={`/work/${next.slug}`} className="pd__forward" data-testid="pd-forward">
        <span className="pd__forward-eyebrow">Next Project</span>
        <span className="pd__forward-name">{next.name}</span>
        <svg
          width="22"
          height="10"
          viewBox="0 0 22 10"
          aria-hidden="true"
          className="pd__forward-arrow"
        >
          <path
            d="M1 5h20M17 1l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Link>
    )}
  </nav>
);

// ---------- Main component -----------------------------------------

export default function ProjectDetail({ project }) {
  const reducedMotion = useReducedMotion();
  const rootRef = useRef(null);
  useScrollReveal(rootRef, reducedMotion);

  useEffect(() => {
    document.title = `${project.name} — Ōraṇi`;
    window.scrollTo(0, 0);
  }, [project.name]);

  const base = project.imageBase;

  return (
    <main
      ref={rootRef}
      className="pd"
      data-testid={`project-detail-${project.slug}`}
      aria-label={project.name}
    >
      {/* Slim back link, top-left */}
      <Link to="/work" className="pd__top-back" data-testid="pd-top-back">
        <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
          <path
            d="M21 5H1M5 1L1 5l4 4"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Selected Work
      </Link>

      {/* SECTION 1 — HERO */}
      <section className="pd__hero" data-testid="pd-hero">
        <figure className="pd__hero-figure">
          <Img
            base={base}
            src={project.heroImage}
            alt={`${project.name} — hero`}
            className="pd__hero-img"
            eager
          />
          <div className="pd__hero-veil" aria-hidden="true" />
        </figure>
        <div className="pd__hero-block">
          <h1 className="pd__title" data-pd-reveal data-testid="pd-title">
            {project.name}
          </h1>
          <p
            className="pd__type"
            data-pd-reveal
            style={{ transitionDelay: "140ms" }}
          >
            {project.type} <span aria-hidden="true">/</span> {project.location}
          </p>
          <p
            className="pd__hero-line"
            data-pd-reveal
            style={{ transitionDelay: "260ms" }}
          >
            {project.heroLine}
          </p>
        </div>
      </section>

      {/* SECTION 2 — INTRO + FACTS */}
      <section className="pd__intro" data-testid="pd-intro">
        <div className="pd__intro-grid">
          <p className="pd__intro-text" data-pd-reveal>
            {project.intro}
          </p>
          <dl className="pd__facts" data-pd-reveal style={{ transitionDelay: "180ms" }}>
            <div className="pd__fact">
              <dt>Type</dt>
              <dd>{project.type}</dd>
            </div>
            <div className="pd__fact">
              <dt>Location</dt>
              <dd>{project.location}</dd>
            </div>
            <div className="pd__fact">
              <dt>Scope</dt>
              <dd>{project.scope}</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Walked sections from the data */}
      {project.sections.map((s, idx) => {
        const key = `${s.kind}-${idx}`;
        switch (s.kind) {
          case "immersive_sequence":
            return <ImmersiveSequence key={key} data={s} base={base} />;
          case "paired_details":
            return <PairedDetails key={key} data={s} base={base} />;
          case "private_rooms":
            return <PrivateRooms key={key} data={s} base={base} />;
          case "gather":
            return <Gather key={key} data={s} base={base} />;
          case "function":
            return <FunctionSection key={key} data={s} base={base} />;
          case "process":
            return <Process key={key} data={s} base={base} />;
          case "design_notes":
            return <DesignNotes key={key} data={s} />;
          case "closing":
            return <Closing key={key} data={s} base={base} />;
          default:
            return null;
        }
      })}

      <NextNav next={project.next} />
    </main>
  );
}
